import { partnerNavOverview, partnerNavDeals } from "@/lib/navigation";
import { NavMain } from "@/components/navigation/nav-main";
import { NavUser } from "@/components/navigation/nav-user";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { api } from "@/lib/api";
import { Plus } from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import type { AuthUser } from "@/types/auth";
import type { PartnerNotificationStatsResponse } from "@/types/notifications";
import { useQuery } from "@tanstack/react-query";

export function PartnerSidebar({ user }: { user: AuthUser }) {
  const navigate = useNavigate();

  const { data: notifStats } = useQuery({
    queryKey: ["notification-stats", "partner"],
    queryFn: async () => {
      try {
        const response = await api.get<PartnerNotificationStatsResponse>(
          "/partner/notifications/stats"
        );
        return response.data;
      } catch {
        return { total: 0, unread: 0, important: 0 };
      }
    },
    refetchInterval: 30000,
    retry: false,
  });

  const unreadCount = notifStats?.unread ?? 0;

  const overviewNavItems = useMemo(
    () =>
      partnerNavOverview.map((item) => {
        if (item.url === "/partner/inbox") {
          return { ...item, badge: unreadCount };
        }
        return item;
      }),
    [unreadCount],
  );

  const dealsNavItems = useMemo(
    () =>
      partnerNavDeals.map((item) => {
        if (item.url !== "/partner/sales") return item;
        return {
          ...item,
          action: (
            <div className="absolute top-1/2 right-1.5 -translate-y-1/2 z-10 flex items-center">
              <Tooltip>
                <TooltipTrigger
                  delay={400}
                  render={(triggerProps) => (
                    <Button
                      {...triggerProps}
                      size="icon-sm"
                      aria-label="Submit new sale"
                      className="h-6 w-6 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        navigate("/partner/submit-sale");
                      }}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  )}
                />
                <TooltipContent side="right">Submit new sale</TooltipContent>
              </Tooltip>
            </div>
          ),
        };
      }),
    [navigate],
  );

  return (
    <Sidebar collapsible="offcanvas">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex items-center gap-2 p-2">
              <img src="/images/landnest-logo-green.png" alt="Landnest Homes and Properties" className="h-8 w-auto object-contain" />
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={overviewNavItems} label="Overview" labelClassName="font-medium" />
        <NavMain items={dealsNavItems} label="Deals" labelClassName="font-medium" />
      </SidebarContent>
      <SidebarFooter className="p-2">
        <div className="rounded-lg border border-border bg-background overflow-hidden">
          <div className="p-3 space-y-2.5">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-foreground leading-snug">
                Refer &amp; earn
              </h3>
              <p className="text-xs font-medium text-muted-foreground leading-snug">
                Earn{" "}
                <span className="font-semibold text-foreground tabular-nums tracking-tight">
                  3%
                </span>
                {" "}on every sale they close.
              </p>
            </div>
            <Button
              size="sm"
              className="w-full"
              iconLeft={<Plus className="h-4 w-4" />}
              onClick={() => navigate("/partner/referrals?tab=invite")}
            >
              Invite partner
            </Button>
          </div>
          <div className="border-t border-border/50 p-2">
            <NavUser user={user} variant="flat" />
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
